import { useEffect, useRef, useState } from 'react';
import { useDocumentsPending, useAcceptDocument } from '@/lib/documents/useDocuments';
import { useFinishSignup } from '@/store/signupProgressStore';
import { useSignupTrack } from '@/analytics';
import { primaryCtaClasses } from './auth-form-style';

const TEST_ID_PREFIX = 'auth-signup-terms';

export interface SignupStepTermsProps {
  /** Called once every pending document is accepted, to complete signup. */
  onAccept: (marketingOptIn: boolean) => void;
}

// Legal-document links. These are placeholder routes — they don't resolve to
// real pages yet (same as the pre-existing `/terms`); swap in the canonical
// CDNA URLs once Legal supplies them.
const TERMS_URL = '/terms';
const CDNA_TERMS_URL = '/cdna-terms-of-use';
const CDNA_PRIVACY_URL = '/cdna-privacy-policy';
const CDNA_RULES_URL = '/cdna-rules';
const CDNA_RISK_URL = '/cdna-risk-disclosure';

/** Inline link to a legal document; stops propagation so it doesn't toggle the row. */
function DocLink({ testId, href, children }: { testId: string; href: string; children: React.ReactNode }) {
  return (
    <a
      data-testid={testId}
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="text-purple underline"
    >
      {children}
    </a>
  );
}

/** Single round checkbox row used in the terms list. */
function CheckboxRow({
  testId,
  checked,
  onToggle,
  align = 'center',
  children,
}: {
  testId: string;
  checked: boolean;
  onToggle: () => void;
  /** Vertical alignment of the box against its label — `start` for multi-line labels. */
  align?: 'center' | 'start';
  children: React.ReactNode;
}) {
  return (
    <label className={'flex gap-2.5 cursor-pointer ' + (align === 'start' ? 'items-start' : 'items-center')}>
      <button
        data-testid={testId}
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={onToggle}
        className={
          'shrink-0 size-[26px] rounded-[8px] border-2 flex items-center justify-center transition-colors ' +
          (checked ? 'bg-purple border-purple text-white' : 'bg-surface border-border')
        }
      >
        {checked && (
          <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M2.5 7.5L5.5 10.5L11.5 3.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      <span className="text-base text-ink leading-snug">{children}</span>
    </label>
  );
}

/**
 * Final step — Terms & Conditions acceptance.
 *
 * Shows the legal-consent checkbox (Crown Prediction's Terms of Services plus
 * the CDNA Terms of Use, Privacy Policy & Rules), an optional marketing opt-in,
 * and an "Approve all" select-all. Only the legal-consent box is required to
 * enable the "Start Predicting" button; marketing stays optional. "Approve all"
 * is a convenience that toggles every box at once.
 *
 * On submit it accepts every document the user must still consent to via the
 * standalone `/documents/{key}/accept` endpoint (the same path the standalone
 * `TermsGate` uses). The legal-consent checkbox is the gate; the server records
 * consent against each pending version id. Only once every acceptance succeeds
 * does the flow complete via `onAccept`.
 *
 * The reduced funnel no longer collects an address or calls `/auth/onboarding`
 * — that endpoint also verifies the address and runs the territory gate, which
 * would reject without the address we no longer gather here.
 */
export function SignupStepTerms({ onAccept }: SignupStepTermsProps) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Always refetch on mount: the shared `/documents/pending` query (TermsGate)
  // can otherwise serve a stale/empty cache from before auth settled, leaving the
  // step with no version ids to submit.
  const pendingQuery = useDocumentsPending(true, { refetchOnMount: 'always', staleTime: 0 });
  const accept = useAcceptDocument();
  const finishSignup = useFinishSignup();
  const track = useSignupTrack();

  // The ARC-145 design dropped the "Approve all" control; the analytics event
  // keeps its `approve_all` field, now derived (true when every box is ticked).
  const allApproved = acceptedTerms && marketingOptIn;

  // Gate on `isFetching` (not `isLoading`) so we never submit before the fresh
  // pending list has resolved — `isLoading` is false when stale cache exists.
  const canSubmit = acceptedTerms && !pendingQuery.isFetching && !accept.isPending;

  // An account with nothing left to accept doesn't owe this step at all — it is
  // an existing user who re-entered the funnel (signed up with an email that
  // already has an account, social "sign up" that was really a login, ...).
  // Complete silently instead of showing a terms sheet they already accepted.
  // No `onboarding_terms_accepted` fires: nothing was accepted here. Guarded by
  // `submittedRef` because a real submit prunes the pending cache to empty,
  // which would otherwise re-trigger this with a second onAccept.
  const submittedRef = useRef(false);
  const nothingPending = pendingQuery.isFetched && !pendingQuery.isFetching && (pendingQuery.data ?? []).length === 0;
  useEffect(() => {
    if (!nothingPending || submittedRef.current) return;
    submittedRef.current = true;
    finishSignup();
    onAccept(false);
  }, [nothingPending, finishSignup, onAccept]);

  async function handleSubmit() {
    setSubmitError(null);
    submittedRef.current = true;
    const pending = pendingQuery.data ?? [];

    try {
      // Accept every pending document version, tagged as signup-funnel provenance.
      // `useAcceptDocument` prunes each from the shared pending-docs cache on
      // success, so the standalone TermsGate sees an empty list and stays quiet
      // the moment the modal closes — no flash of a just-accepted HARD doc.
      await Promise.all(
        pending.map((doc) =>
          accept.mutateAsync({ key: doc.key, documentVersionId: doc.documentVersionId, method: 'signup' }),
        ),
      );
      // Onboarding milestone — record each box as a user-choice boolean (C10:
      // keep the control name). The current UI bundles ToS / Privacy / age &
      // territory into one legal-consent checkbox, so those three share
      // `acceptedTerms`; they split into their own props when the design's
      // separate boxes land, with no event-shape change.
      track('onboarding_terms_accepted', {
        terms_of_service: acceptedTerms,
        privacy_policy: acceptedTerms,
        age_and_territory: acceptedTerms,
        commercial_comms: marketingOptIn,
        approve_all: allApproved,
      });
      // Signup is done — clear the resume flag so a later refresh won't reopen
      // the funnel and the gate is free to handle future re-acceptances.
      finishSignup();
      onAccept(marketingOptIn);
    } catch {
      setSubmitError('We could not complete your signup. Please try again.');
    }
  }

  // Hold rendering until the fresh pending list resolves: an existing account is
  // about to auto-complete above, and flashing a terms sheet at them first reads
  // as the very bug this step is fixing (ARC-145 "Remove T&S pop up").
  if (!pendingQuery.isFetched || nothingPending) {
    return (
      <div data-testid={`${TEST_ID_PREFIX}-loading`} className="flex flex-col gap-5 items-center py-10">
        <div className="size-8 rounded-full border-2 border-purple border-t-transparent animate-spin" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 flex-1">
      <h2 className="text-2xl font-bold text-ink text-center">Approve our terms</h2>

      <p className="text-base text-ink text-center leading-snug">To start predicting, please approve the following:</p>

      <div className="flex flex-col gap-5">
        <CheckboxRow
          testId={`${TEST_ID_PREFIX}-terms-checkbox`}
          checked={acceptedTerms}
          onToggle={() => setAcceptedTerms((v) => !v)}
          align="start"
        >
          I approve Crown Prediction&rsquo;s{' '}
          <DocLink testId={`${TEST_ID_PREFIX}-of-service-link`} href={TERMS_URL}>
            Terms of Services
          </DocLink>{' '}
          And CDNA{' '}
          <DocLink testId={`${TEST_ID_PREFIX}-cdna-terms-link`} href={CDNA_TERMS_URL}>
            Terms of Use
          </DocLink>
          ,{' '}
          <DocLink testId={`${TEST_ID_PREFIX}-cdna-privacy-link`} href={CDNA_PRIVACY_URL}>
            Privacy Policy
          </DocLink>
          ,{' '}
          <DocLink testId={`${TEST_ID_PREFIX}-cdna-rules-link`} href={CDNA_RULES_URL}>
            CDNA Rules
          </DocLink>{' '}
          &amp;{' '}
          <DocLink testId={`${TEST_ID_PREFIX}-cdna-risk-link`} href={CDNA_RISK_URL}>
            Risk Disclosure
          </DocLink>
          *
        </CheckboxRow>

        <CheckboxRow
          testId={`${TEST_ID_PREFIX}-marketing-checkbox`}
          checked={marketingOptIn}
          onToggle={() => setMarketingOptIn((v) => !v)}
        >
          I want to receive commercial communications via my email.
        </CheckboxRow>
      </div>

      {submitError && (
        <p data-testid={`${TEST_ID_PREFIX}-error`} className="text-sm text-danger-alt text-center">
          {submitError}
        </p>
      )}

      <button
        data-testid={`${TEST_ID_PREFIX}-accept-button`}
        type="button"
        disabled={!canSubmit}
        onClick={() => void handleSubmit()}
        className={`mt-auto mb-4 ${primaryCtaClasses}`}
      >
        {accept.isPending ? 'Starting…' : 'Start Predicting'}
      </button>
    </div>
  );
}
